import React, { useEffect, useRef, useState } from 'react';
import axios from '../axiosConfig';
import './AccountModal.css';

// A rough guide for the teacher, not a security rule: the server only requires 6+ characters.
function passwordStrength(password) {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    if (password.length < 6) return { level: 1, label: 'Too short' };
    if (score <= 1) return { level: 1, label: 'Weak' };
    if (score === 2) return { level: 2, label: 'Fair' };
    if (score === 3) return { level: 3, label: 'Good' };
    return { level: 4, label: 'Strong' };
}

function PasswordField({ id, label, value, onChange, autoComplete, describedBy, ...rest }) {
    const [visible, setVisible] = useState(false);
    return (
        <div className="account-field">
            <label htmlFor={id}>{label}</label>
            <div className="account-password-input">
                <input id={id} type={visible ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={onChange} aria-describedby={describedBy} {...rest} />
                <button type="button" className="account-reveal" aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`} aria-pressed={visible} onClick={() => setVisible((shown) => !shown)}>
                    {visible ? 'Hide' : 'Show'}
                </button>
            </div>
        </div>
    );
}

export default function AccountModal({ username, pictureUrl = '', onSaved, onClose }) {
    const dialogRef = useRef(null);
    const fileRef = useRef(null);
    const [name, setName] = useState(username);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [picture, setPicture] = useState(null);
    const [picturePreview, setPicturePreview] = useState('');
    const [savedPicture, setSavedPicture] = useState(pictureUrl);
    const [pictureSaving, setPictureSaving] = useState(false);

    useEffect(() => {
        const previouslyFocused = document.activeElement;
        dialogRef.current.showModal();
        return () => previouslyFocused?.focus();
    }, []);

    useEffect(() => { setSavedPicture(pictureUrl); }, [pictureUrl]);

    useEffect(() => {
        if (!picture) {
            setPicturePreview('');
            return undefined;
        }
        const url = URL.createObjectURL(picture);
        setPicturePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [picture]);

    const choosePicture = (file) => {
        setError('');
        setSuccess('');
        if (file && (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024)) {
            setError('Choose a JPEG, PNG, or WebP picture smaller than 10 MB.');
            return;
        }
        setPicture(file || null);
    };

    const cancelPicture = () => {
        setPicture(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    const uploadPicture = async () => {
        if (!picture || pictureSaving) return;
        setPictureSaving(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('profile_picture', picture);
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/upload_profile_picture.php`, formData);
            if (!data.success) throw new Error(data.error || 'Unable to upload profile picture.');
            onSaved(username, data.pictureUrl);
            // Keep showing the new picture until the navbar has fetched the saved copy.
            setSavedPicture(URL.createObjectURL(picture));
            cancelPicture();
            setSuccess('Your new profile picture is saved.');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Unable to upload profile picture.');
        } finally {
            setPictureSaving(false);
        }
    };

    const save = async (event) => {
        event.preventDefault();
        if (saving) return;
        setError('');
        setSuccess('');
        const changingPassword = Boolean(currentPassword || newPassword || confirmation);
        if (name.trim().length < 3) return setError('Name must be at least 3 characters.');
        if (changingPassword && !currentPassword) return setError('Enter your current password.');
        if (changingPassword && newPassword.length < 6) return setError('New password must be at least 6 characters.');
        if (newPassword !== confirmation) return setError('New passwords do not match.');
        setSaving(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/update_profile.php`, {
                username: name.trim(),
                ...(changingPassword ? { current_password: currentPassword, new_password: newPassword, confirm_password: confirmation } : {})
            });
            if (!data.success) throw new Error(data.error || 'Unable to save account changes.');
            localStorage.setItem('username', data.username);
            onSaved(data.username);
            setName(data.username);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmation('');
            setSuccess(changingPassword ? 'Your password and details are updated.' : 'Your account has been updated.');
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Unable to save account changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const strength = passwordStrength(newPassword);
    const confirmState = confirmation ? (confirmation === newPassword ? 'match' : 'mismatch') : null;
    const initial = (name.trim() || username || '?').slice(0, 1).toUpperCase();
    const shownPicture = picturePreview || savedPicture;

    return (
        <dialog ref={dialogRef} className="account-dialog" aria-labelledby="account-title" onCancel={(event) => { event.preventDefault(); if (!saving) onClose(); }}>
            <form onSubmit={save}>
                <header className="account-hero">
                    <button type="button" className="account-close" aria-label="Close account settings" disabled={saving} onClick={onClose}>×</button>
                    <div className={`account-portrait${picturePreview ? ' is-preview' : ''}`}>
                        {shownPicture ? <img src={shownPicture} alt={picturePreview ? 'Selected profile preview' : 'Your profile picture'} /> : <span aria-hidden="true">{initial}</span>}
                        <button type="button" className="account-portrait-edit" aria-label="Choose a new profile picture" disabled={pictureSaving} onClick={() => fileRef.current?.click()}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" />
                            </svg>
                        </button>
                    </div>
                    <div className="account-identity">
                        <h2 id="account-title">My account</h2>
                        <p>{username}</p>
                    </div>
                </header>

                <div className="account-body">
                    <section className="account-section" aria-labelledby="account-photo-heading">
                        <h3 id="account-photo-heading">Profile picture</h3>
                        <input ref={fileRef} id="account-picture" className="account-file" type="file" accept="image/jpeg,image/png,image/webp" aria-label="Profile picture" onChange={(event) => choosePicture(event.target.files?.[0])} />
                        {picture ? (
                            <div className="account-photo-actions">
                                <p>Happy with this one? It&apos;s resized and compressed when you save it.</p>
                                <div>
                                    <button type="button" className="account-primary" disabled={pictureSaving} onClick={uploadPicture}>{pictureSaving ? 'Saving picture…' : 'Use this picture'}</button>
                                    <button type="button" disabled={pictureSaving} onClick={cancelPicture}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <div className="account-photo-actions">
                                <p>A clear photo of your face helps colleagues recognise you. JPEG, PNG or WebP, up to 10 MB.</p>
                                <div>
                                    <button type="button" onClick={() => fileRef.current?.click()}>{savedPicture ? 'Change picture' : 'Upload a picture'}</button>
                                </div>
                            </div>
                        )}
                    </section>

                    <fieldset className="account-section" disabled={saving}>
                        <legend>Your name</legend>
                        <div className="account-field">
                            <label htmlFor="account-name">Name / username</label>
                            <input id="account-name" autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} required minLength={3} maxLength={100} aria-describedby="account-name-hint" />
                            <p id="account-name-hint" className="account-hint">This is also your login username. You can always sign in with your email instead.</p>
                        </div>
                    </fieldset>

                    <fieldset className="account-section" disabled={saving}>
                        <legend>Change password</legend>
                        <p className="account-hint">Leave these blank to keep your current password.</p>
                        <PasswordField id="account-current-password" label="Current password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                        <PasswordField id="account-new-password" label="New password" autoComplete="new-password" minLength={6} maxLength={72} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} describedBy="account-password-hint" />
                        <div className="account-strength" aria-live="polite">
                            <div className={`account-strength-bar level-${strength?.level || 0}`} aria-hidden="true"><span /><span /><span /><span /></div>
                            <p id="account-password-hint" className="account-hint">
                                {strength ? <><strong>{strength.label}.</strong> </> : null}
                                Use at least 6 characters. Longer, with numbers or symbols, is safer.
                            </p>
                        </div>
                        <PasswordField id="account-confirm-password" label="Confirm new password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} describedBy="account-confirm-hint" />
                        <p id="account-confirm-hint" className={`account-hint account-confirm ${confirmState || ''}`} aria-live="polite">
                            {confirmState === 'match' ? '✓ Passwords match' : confirmState === 'mismatch' ? 'Passwords don’t match yet' : ' '}
                        </p>
                    </fieldset>
                </div>

                <footer className="account-footer">
                    {error && <p className="account-message account-error" role="alert">{error}</p>}
                    {success && <p className="account-message account-success" role="status">{success}</p>}
                    <div className="account-actions">
                        <button type="button" disabled={saving} onClick={onClose}>Close</button>
                        <button type="submit" className="account-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                    </div>
                </footer>
            </form>
        </dialog>
    );
}
